import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { dateOnlyToDate, dateToDateOnly, dateToRFC3339 } from "@/lib/format.ts";
import { useCreateObra } from "../../hooks/use-create-obra.ts";
import {
	type ObraFormValues,
	obraSchema,
} from "../../schemas/obra-schema.ts";

const VIOLATION_FIELDS: Record<string, keyof ObraFormValues> = {
	name: "name",
	description: "description",
	start_date: "startDate",
	expected_end_date: "expectedEndDate",
};

export function ObraFormDialog({
	open,
	onOpenChange,
	onCreated,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreated?: (constructionId: string) => void;
}) {
	const [formError, setFormError] = useState<string | null>(null);
	const createConstruction = useCreateObra();
	const isPending = createConstruction.isPending;

	const {
		register,
		control,
		handleSubmit,
		reset,
		setError,
		formState: { errors },
	} = useForm<ObraFormValues>({
		resolver: zodResolver(obraSchema),
		defaultValues: {
			name: "",
			description: "",
			startDate: "",
			expectedEndDate: "",
		},
	});

	useEffect(() => {
		if (open) {
			reset({ name: "", description: "", startDate: "", expectedEndDate: "" });
			setFormError(null);
		}
	}, [open, reset]);

	function applyAPIError(err: unknown, fallback: string) {
		if (err instanceof APIError) {
			let matchedField = false;
			for (const [field, message] of Object.entries(err.violations)) {
				const formField = VIOLATION_FIELDS[field];
				if (formField) {
					setError(formField, { message });
					matchedField = true;
				}
			}
			if (!matchedField) setFormError(err.message || fallback);
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: ObraFormValues) {
		setFormError(null);
		try {
			const construction = await createConstruction.mutateAsync({
				name: values.name,
				description: values.description?.trim() || undefined,
				startDate: dateToRFC3339(values.startDate),
				expectedEndDate: dateToRFC3339(values.expectedEndDate),
			});
			toast.success("Obra criada com sucesso!");
			onOpenChange(false);
			onCreated?.(construction.id);
		} catch (err) {
			applyAPIError(err, "Não foi possível criar a obra.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Nova obra</DialogTitle>
					<DialogDescription>
						Cadastre uma obra neste canteiro para acompanhar suas etapas.
					</DialogDescription>
				</DialogHeader>
				<form
					id="construction-form"
					className="construction-form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<div className="form-group">
						<Label htmlFor="construction-name">Nome da obra</Label>
						<Input
							id="construction-name"
							placeholder="Ex.: Bloco A"
							aria-invalid={!!errors.name}
							{...register("name")}
						/>
						{errors.name && <p className="form-error">{errors.name.message}</p>}
					</div>

					<div className="form-group">
						<Label htmlFor="construction-description">
							Descrição (opcional)
						</Label>
						<Textarea
							id="construction-description"
							placeholder="Detalhes da obra"
							{...register("description")}
						/>
					</div>

					<div className="construction-form__dates">
						<div className="form-group">
							<Label htmlFor="construction-start">Início previsto</Label>
							<Controller
								control={control}
								name="startDate"
								render={({ field }) => (
									<DatePicker
										id="construction-start"
										modal
										name={field.name}
										selected={dateOnlyToDate(field.value)}
										onSelected={(date) => field.onChange(dateToDateOnly(date))}
										aria-invalid={!!errors.startDate}
									/>
								)}
							/>
						</div>
						<div className="form-group">
							<Label htmlFor="construction-end">Entrega prevista</Label>
							<Controller
								control={control}
								name="expectedEndDate"
								render={({ field }) => (
									<DatePicker
										id="construction-end"
										modal
										name={field.name}
										selected={dateOnlyToDate(field.value)}
										onSelected={(date) => field.onChange(dateToDateOnly(date))}
										aria-invalid={!!errors.expectedEndDate}
									/>
								)}
							/>
						</div>
					</div>

					{formError && (
						<p role="alert" className="form-error-alert">
							{formError}
						</p>
					)}
				</form>
				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={isPending}
					>
						Cancelar
					</Button>
					<Button type="submit" form="construction-form" disabled={isPending}>
						{isPending && <Loader2 className="construction-spinner" />}
						Criar obra
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
